import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export function NDISVerificationForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    ndisNumber: '',
    phoneNumber: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('/api/ndis-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        toast.success('Verification request submitted! Check your email for confirmation.');
        setFormData({ name: '', email: '', ndisNumber: '', phoneNumber: '' });
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to submit verification. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-green-800 uppercase mb-2">Request Received!</h2>
          <p className="text-green-700 mb-6">
            Your NDIS/PWD verification request has been submitted. You'll receive a confirmation email shortly.
          </p>
          <div className="bg-white rounded-xl p-4 text-left text-sm text-green-800">
            <p className="font-bold mb-2">What happens next:</p>
            <ol className="list-decimal list-inside space-y-1 text-green-700">
              <li>Review within 24 hours</li>
              <li>Additional docs if needed</li>
              <li>Approval email with lifetime pricing</li>
            </ol>
          </div>
          <Button
            onClick={() => setSubmitted(false)}
            className="mt-6 bg-green-600 hover:bg-green-700"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
          <AlertCircle className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 uppercase mb-2">
          NDIS/PWD Verification
        </h2>
        <p className="text-slate-600 text-sm">
          Get lifetime access pricing for persons with disability or NDIS provider rates
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
            Full Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none transition-colors"
            placeholder="John Smith"
            required
          />
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
            Email Address *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none transition-colors"
            placeholder="john@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none transition-colors"
            placeholder="0400 000 000"
          />
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
            NDIS Number (if applicable)
          </label>
          <input
            type="text"
            value={formData.ndisNumber}
            onChange={(e) => setFormData({ ...formData, ndisNumber: e.target.value })}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none transition-colors"
            placeholder="NXXXXXXXX"
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-bold text-amber-800">
            📧 You'll receive a confirmation email immediately. Our team will review your request within 24 hours.
          </p>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit for Verification'
          )}
        </Button>
      </form>
    </div>
  );
}
